import { Clock, LogIn, Coffee, Sunset, LogOut } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { useTimeEntry } from '../hooks/useTimeEntry';
import { getClockStatus, formatTimeBRT, formatDateBRT } from '../types';
import { useState, useEffect } from 'react';

// ==========================================
// Config visual por status
// ==========================================
const getStatusConfig = (breakEndMinimumTime?: string) => ({
  idle: {
    label: 'Aguardando entrada',
    sub: 'Registre sua entrada para iniciar o expediente',
    color: 'text-muted-foreground',
    ring: 'ring-border',
    dot: 'bg-muted-foreground',
  },
  'clocked-in': {
    label: 'Trabalhando',
    sub: 'Expediente em andamento',
    color: 'text-green-600 dark:text-green-400',
    ring: 'ring-green-500/40',
    dot: 'bg-green-500 animate-pulse',
  },
  'on-break': {
    label: 'Em intervalo',
    sub: breakEndMinimumTime
      ? `Retorne após as ${breakEndMinimumTime}`
      : 'Retorne quando estiver pronto',
    color: 'text-amber-600 dark:text-amber-400',
    ring: 'ring-amber-500/40',
    dot: 'bg-amber-500 animate-pulse',
  },
  'clocked-out': {
    label: 'Expediente encerrado',
    sub: 'Bom trabalho! Até amanhã.',
    color: 'text-blue-600 dark:text-blue-400',
    ring: 'ring-blue-500/40',
    dot: 'bg-blue-500',
  },
});

// ==========================================
// Step de horário — sempre ocupa slot, vazio se não registrado
// ==========================================
function TimeStep({
  label,
  time,
  isLast,
}: {
  label: string;
  time: string | null | undefined;
  isLast?: boolean;
}) {
  const registered = !!time;
  return (
    <div className="flex items-center flex-1 min-w-0">
      {/* Conteúdo do step */}
      <div className="flex flex-col items-center gap-0.5 flex-1 min-w-0">
        <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide truncate w-full text-center">
          {label}
        </span>
        {registered ? (
          <span className="font-mono text-sm font-semibold text-foreground">{formatTimeBRT(time)}</span>
        ) : (
          <span className="font-mono text-sm text-muted-foreground/40">--:--</span>
        )}
      </div>
      {/* Separador entre steps */}
      {!isLast && (
        <span className="mx-1 text-muted-foreground/30 text-base shrink-0">›</span>
      )}
    </div>
  );
}

// ==========================================
// Botão de ação
// ==========================================
interface ActionButtonProps {
  onClick: () => void;
  disabled: boolean;
  loading: boolean;
  icon: React.ElementType;
  label: string;
  loadingLabel: string;
  variant: 'green' | 'amber' | 'red' | 'muted';
}

function ActionButton({ onClick, disabled, loading, icon: Icon, label, loadingLabel, variant }: ActionButtonProps) {
  const variants = {
    green: 'bg-green-600 hover:bg-green-700 text-white dark:bg-green-700 dark:hover:bg-green-600',
    amber: 'bg-amber-500 hover:bg-amber-600 text-white dark:bg-amber-600 dark:hover:bg-amber-500',
    red: 'bg-red-600 hover:bg-red-700 text-white dark:bg-red-700 dark:hover:bg-red-600',
    muted: 'bg-muted text-muted-foreground cursor-not-allowed opacity-40',
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold transition-all',
        disabled ? variants.muted : variants[variant]
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {loading ? loadingLabel : label}
    </button>
  );
}

// ==========================================
// ClockWidget
// ==========================================
export function ClockWidget() {
  const {
    todayEntry,
    isLoadingToday,
    clockIn,
    isClockingIn,
    breakStart,
    isBreakStarting,
    breakEnd,
    isBreakEnding,
    clockOut,
    isClockingOut,
  } = useTimeEntry();

  const [currentTime, setCurrentTime] = useState(new Date());

  // Atualiza o tempo a cada minuto para recalcular se já pode voltar
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // 1 minuto
    return () => clearInterval(interval);
  }, []);

  const status = getClockStatus(todayEntry ?? null);
  const today = new Date().toISOString();

  // Calcula se já pode voltar do almoço (1 hora mínima CLT)
  let canReturnFromBreak = true;
  let breakEndMinimumTime: string | undefined;

  if (todayEntry?.breakStart && !todayEntry.breakEnd) {
    const breakStartTime = new Date(todayEntry.breakStart);
    const oneHourMs = 60 * 60 * 1000;
    const breakEndMinimum = new Date(breakStartTime.getTime() + oneHourMs);

    canReturnFromBreak = currentTime.getTime() >= breakEndMinimum.getTime();

    if (!canReturnFromBreak) {
      breakEndMinimumTime = breakEndMinimum.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'America/Sao_Paulo',
      });
    }
  }

  const config = getStatusConfig(breakEndMinimumTime)[status];

  if (isLoadingToday) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-6 animate-pulse">
        <div className="h-32 w-32 rounded-full bg-muted" />
        <div className="h-5 w-48 rounded bg-muted" />
        <div className="grid grid-cols-2 gap-3 w-full max-w-xs">
          {[...Array(4)].map((_, i) => <div key={i} className="h-11 rounded-xl bg-muted" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-8 py-6 px-4">

      {/* Data */}
      <p className="text-sm text-muted-foreground capitalize tracking-wide">
        {formatDateBRT(today)}
      </p>

      {/* Anel de status + ícone central */}
      <div className={cn(
        'relative flex items-center justify-center h-36 w-36 rounded-full ring-8 transition-all duration-500',
        config.ring,
        'bg-card shadow-md'
      )}>
        <Clock className={cn('h-14 w-14', config.color)} strokeWidth={1.5} />
        {/* Dot animado */}
        <span className={cn(
          'absolute bottom-3 right-3 h-3.5 w-3.5 rounded-full ring-2 ring-background',
          config.dot
        )} />
      </div>

      {/* Status texto */}
      <div className="text-center space-y-1">
        <p className={cn('text-xl font-bold', config.color)}>{config.label}</p>
        <p className="text-sm text-muted-foreground">{config.sub}</p>
      </div>

      {/* Timeline de horários do dia — 4 slots fixos, nunca quebra linha */}
      {todayEntry && (
        <div className="w-full max-w-sm rounded-xl border bg-muted/30 px-4 py-3">
          <div className="flex items-center w-full">
            <TimeStep label="Entrada"      time={todayEntry.clockIn}    />
            <TimeStep label="Saída almoço" time={todayEntry.breakStart} />
            <TimeStep label="Volta almoço" time={todayEntry.breakEnd}   />
            <TimeStep label="Saída"        time={todayEntry.clockOut}   isLast />
          </div>
        </div>
      )}

      {/* Botões de ação — só o próximo passo ativo fica em destaque */}
      <div className="grid grid-cols-2 gap-3 w-full max-w-sm">
        <ActionButton
          onClick={() => clockIn()}
          disabled={status !== 'idle'}
          loading={isClockingIn}
          icon={LogIn}
          label="Registrar Entrada"
          loadingLabel="Registrando..."
          variant={status === 'idle' ? 'green' : 'muted'}
        />
        <ActionButton
          onClick={() => breakStart()}
          disabled={status !== 'clocked-in' || !!todayEntry?.breakStart}
          loading={isBreakStarting}
          icon={Coffee}
          label="Saída Almoço"
          loadingLabel="Registrando..."
          variant={status === 'clocked-in' && !todayEntry?.breakStart ? 'amber' : 'muted'}
        />
        <ActionButton
          onClick={() => breakEnd()}
          disabled={status !== 'on-break' || !canReturnFromBreak}
          loading={isBreakEnding}
          icon={Sunset}
          label="Volta Almoço"
          loadingLabel="Registrando..."
          variant={status === 'on-break' && canReturnFromBreak ? 'amber' : 'muted'}
        />
        <ActionButton
          onClick={() => clockOut()}
          disabled={status !== 'clocked-in' && status !== 'on-break'}
          loading={isClockingOut}
          icon={LogOut}
          label="Registrar Saída"
          loadingLabel="Registrando..."
          variant={status === 'clocked-in' || status === 'on-break' ? 'red' : 'muted'}
        />
      </div>
    </div>
  );
}
